import { useMemo, useState } from "react";
import { Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { FunctionField, useDataProvider, useGetList, useGetMany } from "react-admin";
import { useQueries } from "@tanstack/react-query";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { TabContent } from "@/components/common/PageLayout";
import { ClaimItem, Claimant, ResourceName, StoplossClaim } from "@/types";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { auraTrackChip, auraTrackState, carrierTrackChip } from "@/resources/stoploss-claims/twoTrackState";
import {
  countsDespiteStamp,
  shortIneligibleReason,
} from "@/resources/stoploss-claims/components/StoplossClaimItemsTab";
import { describeIneligibleReason } from "@/resources/stoploss-claims/claimItems/components/EligibilityBanner";
import { useClaimantPage } from "../hooks/useClaimantPage";

const ITEMS_PER_PAGE = 25;

const money = (n?: number) => (typeof n === "number" ? formatUSD(n) : "—");
const day = (d?: string | null) => (d ? formatDate(d.slice(0, 10)) : "—");

/** The card clips overflow, so the table scrolls sideways inside it when the column is narrow (the claim page's rule). */
const Scroller = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ overflowX: "auto", "& table": { minWidth: 640 } }}>{children}</Box>
);

/** The claim page's table chrome: a title strip above the datagrid, a summary strip below. */
const STRIP_SX = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 2,
  px: 2,
  py: 1.25,
  flexWrap: "wrap",
} as const;

const TableStrip = ({
  title,
  aside,
  position,
  testId,
}: {
  title: React.ReactNode;
  aside?: React.ReactNode;
  position: "top" | "bottom";
  testId?: string;
}) => (
  <Box
    data-testid={testId}
    sx={{ ...STRIP_SX, [position === "top" ? "borderBottom" : "borderTop"]: "1px solid", borderColor: "divider" }}
  >
    <Typography variant="body2" fontWeight={600} component="div">
      {title}
    </Typography>
    {aside ? (
      <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {aside}
      </Typography>
    ) : null}
  </Box>
);

type ClaimRow = StoplossClaim & { claimant_name?: string };

/**
 * The claimant's stop-loss claims on the selected policy (Feature #7659, #7660
 * AC7): one row per claim with its amount, the two track states as chips and
 * its status. Rows open the claim page, which owns the actions; nothing is
 * submitted from here. In a family view the member's name is a column.
 */
export const ClaimantClaimsTab = () => {
  const {
    claimantId,
    policyId,
    claims,
    claimsTotal,
    claimsLoading,
    claimsError,
    claimsAmount,
    accelEnabled,
    familyLayout,
    familyUnit,
  } = useClaimantPage();

  const memberIds = useMemo(
    () =>
      familyLayout ? (familyUnit.data?.members ?? []).map((m) => String(m.id)).filter((id) => id !== claimantId) : [],
    [familyLayout, familyUnit.data, claimantId]
  );
  // Family view: the other members' claims on the same policy, one bounded query
  // per member (a unit is a handful of people), instead of every claim on the
  // client's policy filtered client-side (review !5050, medium 6).
  const dataProvider = useDataProvider();
  const memberQueries = useQueries({
    queries: memberIds.map((id) => ({
      queryKey: ["claimant-page-member-claims", id, policyId],
      enabled: !!policyId,
      queryFn: async () => {
        const { data } = await dataProvider.getList<StoplossClaim>(ResourceName.STOPLOSS_CLAIMS, {
          filter: { claimant_id_ref: id, policy_id_ref: policyId },
          pagination: { page: 1, perPage: 100 },
          sort: { field: "created_datetime", order: "ASC" },
        });
        return data;
      },
    })),
  });
  const memberStamp = memberQueries.map((q) => q.dataUpdatedAt).join(",");
  const rows = useMemo<ClaimRow[]>(() => {
    if (!familyLayout) return claims;
    const own = new Set(claims.map((c) => String(c.id)));
    const others = memberQueries.flatMap((q) => q.data ?? []).filter((c) => !own.has(String(c.id)));
    return [...claims, ...others];
  }, [claims, memberStamp, familyLayout]);
  const nameIds = useMemo(
    () => (familyLayout ? [...new Set(rows.map((c) => c.merged_into_claimant_id_ref ?? c.claimant_id_ref))] : []),
    [rows, familyLayout]
  );
  const { data: claimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: nameIds },
    { enabled: nameIds.length > 0 }
  );
  const nameOf = useMemo(() => new Map(claimants.map((c) => [String(c.id), c.claimant_name])), [claimants]);

  const who = (c: ClaimRow) => nameOf.get(c.merged_into_claimant_id_ref ?? c.claimant_id_ref) ?? "—";

  // Every status: the netting set counts denied claims too.
  const total = rows.reduce((s, c) => s + (c.total_paid_amount ?? 0), 0);
  const count = familyLayout ? rows.length : (claimsTotal ?? claims.length);

  return (
    <TabContent>
      <Stack spacing={2}>
        <SectionCard compact noPadding noDivider>
          <TableStrip
            position="top"
            testId="claims-strip"
            title={
              familyLayout
                ? "Stop-loss claims in this family on the policy"
                : policyId
                  ? "Stop-loss claims on this policy"
                  : "Stop-loss claims"
            }
            aside={claimsLoading ? undefined : `${count} · ${formatUSD(familyLayout ? total : claimsAmount)}`}
          />
          {claimsLoading ? (
            <Stack spacing={1} sx={{ p: 2 }} data-testid="claims-loading">
              <Skeleton variant="rounded" height={36} />
              <Skeleton variant="rounded" height={36} />
            </Stack>
          ) : claimsError ? (
            <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
              Couldn&apos;t load the stop-loss claims.
            </Typography>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 5, px: 2, textAlign: "center" }} data-testid="claims-empty">
              <Typography variant="body2" color="text.secondary">
                {policyId ? "No stop-loss claims on this policy." : "No stop-loss claims."}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                A claim is created when spend crosses the deductible.
              </Typography>
            </Box>
          ) : (
            <Scroller>
              <PaginatedDatagrid
                data={rows}
                initialPerPage={ITEMS_PER_PAGE}
                rowClick={(id) => `/stoploss-claims/${id}/show`}
                resource={ResourceName.STOPLOSS_CLAIMS}
              >
                {familyLayout ? (
                  <FunctionField
                    label="Claimant"
                    render={(c: ClaimRow) => (
                      <Typography variant="body2" noWrap>
                        {who(c)}
                      </Typography>
                    )}
                  />
                ) : null}
                <FunctionField
                  label="Claim"
                  render={(c: ClaimRow) => (
                    <Box>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 320 }}>
                        {c.stoploss_claim_type ? `${formatToTitleCase(c.stoploss_claim_type)} claim` : "Claim"}
                      </Typography>
                      {c.merged_into_claimant_id_ref ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          From merged record
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                />
                <FunctionField
                  label="Paid"
                  render={(c: ClaimRow) => (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {c.first_paid_date || c.last_paid_date
                        ? `${day(c.first_paid_date)} – ${day(c.last_paid_date)}`
                        : "—"}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="Claim Amount"
                  textAlign="right"
                  render={(c: ClaimRow) => (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(c.total_paid_amount)}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="Aura Funding"
                  render={(c: ClaimRow) => {
                    if (!accelEnabled)
                      return (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      );
                    const chip = auraTrackChip(c);
                    if (chip) return <StatusChip label={chip.label} color={chip.color} size={UIComponentSize.Small} />;
                    return (
                      <StatusChip
                        label={auraTrackState(c) === "rejected" ? "Rejected" : "Not requested"}
                        color={auraTrackState(c) === "rejected" ? ColorType.Error : ColorType.Default}
                        size={UIComponentSize.Small}
                      />
                    );
                  }}
                />
                <FunctionField
                  label="Carrier"
                  render={(c: ClaimRow) => {
                    const chip = carrierTrackChip(c);
                    return chip ? (
                      <StatusChip label={chip.label} color={chip.color} size={UIComponentSize.Small} />
                    ) : (
                      <StatusChip label="Not submitted" color={ColorType.Default} size={UIComponentSize.Small} />
                    );
                  }}
                />
                <FunctionField
                  label="Status"
                  render={(c: ClaimRow) => (
                    <StatusChip
                      label={formatToTitleCase(c.claim_status || "—")}
                      color={getStatusColor(c.claim_status) ?? ColorType.Default}
                      size={UIComponentSize.Small}
                    />
                  )}
                />
              </PaginatedDatagrid>
            </Scroller>
          )}
        </SectionCard>
        <NotEligibleItems />
      </Stack>
    </TabContent>
  );
};

/**
 * Medical / Rx claims (claim items in storage) the basis stamp marked not eligible on this policy, with the
 * reason. Two reasons still count toward spend or on another term (the claim
 * page's "Counts" ruling); the rest do not. Rows open the claim item page.
 * Server-paged with the true count (no 10,000-row fetch).
 */
const NotEligibleItems = () => {
  const { claimantId, policyId, isFamilyPolicy, claimant } = useClaimantPage();
  // On a family-deductible policy the stamp's `over` input is the individual
  // excess, so lines feeding the family claim are stamped below_deductible.
  const familyPool = isFamilyPolicy && !!claimant.subscriber_ref_id;
  const [page, setPage] = useState(0);
  const filter = useMemo(() => {
    const f: Record<string, unknown> = { claimant_id_ref: claimantId, is_eligible: false };
    if (policyId) f.policy_id_ref = policyId;
    return f;
  }, [claimantId, policyId]);
  const {
    data = [],
    total = 0,
    isLoading,
    error,
  } = useGetList<ClaimItem>(
    ResourceName.CLAIM_ITEMS,
    {
      filter,
      pagination: { page: page + 1, perPage: ITEMS_PER_PAGE },
      sort: { field: "date_of_service", order: "DESC" },
    },
    { enabled: !!claimantId }
  );

  const countsNote = (reasons: string[]) =>
    reasons.includes("owned_by_other_term")
      ? "counts toward the other term"
      : familyPool
        ? "counts toward the family pool"
        : "counts toward spend";

  // Nothing until the answer is in: most claimants have none, and a skeleton card
  // that then vanishes reads as a blink (Dillon, 2026-10-28). The claims table
  // above carries the loading state for the tab.
  if (isLoading || (!error && total === 0)) return null;

  return (
    <SectionCard compact noPadding noDivider>
      <TableStrip
        position="top"
        testId="not-eligible-strip"
        title={policyId ? "Claims not eligible on this policy" : "Claims not eligible"}
        aside={String(total)}
      />
      {error ? (
        <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
          Couldn&apos;t load the claims.
        </Typography>
      ) : (
        <Scroller>
          <PaginatedDatagrid
            data={data}
            serverSide
            totalCount={total}
            page={page}
            onPageChange={setPage}
            rowsPerPage={ITEMS_PER_PAGE}
            enablePagination={total > ITEMS_PER_PAGE}
            rowClick={(id) => `/claim-items/${id}/show`}
            resource={ResourceName.CLAIM_ITEMS}
          >
            <FunctionField
              label="Service Date"
              render={(i: ClaimItem) => (
                <Typography variant="body2" noWrap>
                  {day(i.date_of_service)}
                </Typography>
              )}
            />
            <FunctionField
              label="Provider"
              render={(i: ClaimItem) => (
                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 260 }}>
                  {i.provider || "—"}
                </Typography>
              )}
            />
            <FunctionField
              label="Status"
              render={(i: ClaimItem) => {
                const reasons = i.ineligible_reasons ?? [];
                const counts = countsDespiteStamp(reasons);
                return (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    <Tooltip title={counts ? countsNote(reasons) : ""}>
                      <span>
                        <StatusChip
                          label={counts ? "Counts" : "Not eligible"}
                          color={counts ? "info" : "muted"}
                          size={UIComponentSize.Small}
                        />
                      </span>
                    </Tooltip>
                    {reasons.map((code) => (
                      <Tooltip key={code} title={describeIneligibleReason(code)}>
                        <span>
                          <StatusChip
                            label={shortIneligibleReason(code)}
                            color={ColorType.Default}
                            size={UIComponentSize.Small}
                          />
                        </span>
                      </Tooltip>
                    ))}
                  </Box>
                );
              }}
            />
            <FunctionField
              label="Paid"
              textAlign="right"
              render={(i: ClaimItem) => {
                const muted = !countsDespiteStamp(i.ineligible_reasons ?? []);
                return (
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: muted ? "text.secondary" : "text.primary",
                      textDecoration: muted ? "line-through" : "none",
                    }}
                  >
                    {money(i.paid_amount)}
                  </Typography>
                );
              }}
            />
          </PaginatedDatagrid>
        </Scroller>
      )}
    </SectionCard>
  );
};
